import { useMemo, useState } from "react";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { ChevronRight, ChevronLeft, Check, Settings, RefreshCwIcon } from "lucide-react";
import { AppButton, ConfirmationModal, FigmaTypography } from "@libs/zapi-ui/custom";
import { Popover, PopoverAnchor, PopoverContent, Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import type { TFunction } from "i18next";

export type TBriefStatus = "empty" | "incomplete" | "complete";

type TProjectCreationHeaderProps = {
  title?: string;
  // The project's own chosen language, not the viewer's UI language — this header's labels
  // and dialogs follow the project being created, same as the chat beside it.
  language: string | null;
  progress: number;
  briefStatus: TBriefStatus;
  canReset: boolean;
  isLaunching?: boolean;
  disabled?: boolean;
  primaryAction?: "launch" | "next";
  nextDisabled?: boolean;
  // Next waits on a question in the chat, not on the form.
  nextBlockedByChat?: boolean;
  onLaunch: () => void;
  onNext?: () => void;
  onBack?: () => void;
  onReset: () => void;
  onOpenSettings?: () => void;
  readOnly?: boolean;
  // The brief covers the essentials: the progress label says so instead of a percentage.
  readyToLaunch?: boolean;
  // A one-time pointer at Launch Analysis once the essentials are covered.
  launchNudge?: { open: boolean; onDismiss: () => void };
};

type TCreationT = TFunction<("createProject" | "common")[], undefined>;

export const launchConfirmCopy = (t: TCreationT, briefStatus: TBriefStatus) =>
  briefStatus === "incomplete"
    ? { title: t("header.launchIncompleteTitle"), description: t("header.launchIncompleteDescription") }
    : { title: t("header.launchCompleteTitle"), description: t("header.launchCompleteDescription") };

const launchTooltipByStatus = (t: TCreationT): Record<TBriefStatus, { title: string; description: string }> => ({
  empty: { title: t("header.frameEmptyTitle"), description: t("header.frameEmptyDescription") },
  incomplete: { title: t("header.frameIncompleteTitle"), description: t("header.frameIncompleteDescription") },
  complete: { title: t("header.frameCompleteTitle"), description: t("header.frameCompleteDescription") }
});

function ProgressIndicator({ value, complete }: { value: number; complete: boolean }) {
  if (complete) {
    return <Check className="size-4 text-innoveraGreen-500" strokeWidth={2} />;
  }

  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, value));
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <svg className="size-4 text-innoveraZinc-500" viewBox="0 0 16 16" aria-hidden>
      <circle cx="8" cy="8" r={radius} strokeWidth={1.5} stroke="currentColor" fill="none" opacity="0.25" />
      <circle
        cx="8"
        cy="8"
        r={radius}
        strokeWidth={1.5}
        stroke="currentColor"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 8 8)"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ProjectCreationHeader({
  title,
  language,
  progress,
  briefStatus,
  canReset,
  isLaunching = false,
  disabled = false,
  primaryAction = "launch",
  nextDisabled = false,
  nextBlockedByChat = false,
  onLaunch,
  onNext,
  onBack,
  onReset,
  onOpenSettings,
  readOnly = false,
  readyToLaunch = false,
  launchNudge
}: TProjectCreationHeaderProps) {
  const t = useMemo(
    () => i18n.getFixedT(projectLanguageToLng(language), ["createProject", "common"]),
    [language]
  );
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLaunchConfirm, setShowLaunchConfirm] = useState(false);

  const isComplete = progress >= 100;
  const isLaunchDisabled = briefStatus === "empty" || disabled || isLaunching;

  const handleLaunchClick = () => {
    setShowLaunchConfirm(true);
  };

  const handleLaunchConfirm = () => {
    setShowLaunchConfirm(false);
    onLaunch();
  };

  const handleResetConfirm = () => {
    setShowResetConfirm(false);
    onReset();
  };

  const launchTooltip = launchTooltipByStatus(t)[briefStatus];
  const launchConfirm = launchConfirmCopy(t, briefStatus);

  return (
    <>
      <div className="bg-white border-b border-innoveraZinc-100 flex items-center justify-between px-4 py-3 shrink-0">
        <p className="flex-1 min-w-0 truncate text-[16px] font-medium leading-[25px] tracking-[-0.16px] text-innoveraZinc-900">
          {title ?? t("newProjectTitle")}
        </p>

        <div className="flex flex-1 items-center justify-end gap-3 min-w-fit">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <ProgressIndicator value={progress} complete={isComplete} />
              <FigmaTypography component="span" variant="body-3" className="whitespace-nowrap text-innoveraZinc-500">
                {readyToLaunch ? t("header.readyToLaunch") : t("header.percentComplete", { value: Math.round(progress) })}
              </FigmaTypography>
            </div>

            {!readOnly && (
              <AppButton
                appearance="outlined"
                color="zinc150"
                size="icon"
                disabled={!canReset}
                onClick={() => setShowResetConfirm(true)}
                aria-label={t("header.resetProject")}
                startAdornment={<RefreshCwIcon className="size-4" />}
                title={t("header.resetProject")}
                data-testid="project-creation-header-reset-button"
              />
            )}
          </div>

          {!readOnly && (
            <>
              <div className="h-4 w-px bg-innoveraZinc-200" />

              {primaryAction === "launch" && onBack && (
                <AppButton
                  appearance="text"
                  color="zinc150"
                  onClick={onBack}
                  startAdornment={<ChevronLeft className="size-4" />}
                  data-testid="project-creation-header-back-button"
                >
                  {t("header.back")}
                </AppButton>
              )}

              {onOpenSettings && (
                <AppButton
                  appearance="outlined"
                  color="zinc150"
                  size="icon"
                  onClick={onOpenSettings}
                  aria-label={t("header.analysisSettings")}
                  startAdornment={<Settings className="size-4" />}
                  title={t("header.analysisSettings")}
                  data-testid="project-creation-header-settings-button"
                />
              )}

              {primaryAction === "next" ? (
                <Tooltip open={nextDisabled ? undefined : false}>
                  <TooltipTrigger asChild>
                    <span>
                      <AppButton
                        color={nextDisabled ? "zinc100" : "zinc950"}
                        onClick={onNext}
                        disabled={nextDisabled || disabled}
                        endAdornment={<ChevronRight className="size-4" />}
                        data-testid="project-creation-header-next-step-button"
                      >
                        {t("nextStep")}
                      </AppButton>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="end" sideOffset={8} className="w-[252px]">
                    <div className="flex flex-col gap-1.5">
                      <FigmaTypography variant="label-2" color="zinc900">{t("header.setupIncompleteTitle")}</FigmaTypography>
                      <FigmaTypography variant="body-3" color="zinc600">
                        {t(nextBlockedByChat ? "header.setupAnswerInChatDescription" : "header.setupIncompleteDescription")}
                      </FigmaTypography>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Popover open={!!launchNudge?.open}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverAnchor asChild>
                    <span>
                      <AppButton
                        color={briefStatus === "complete" ? "zinc950" : "zinc100"}
                        onClick={handleLaunchClick}
                        disabled={isLaunchDisabled}
                        isLoading={isLaunching}
                        loadingText={t("header.launching")}
                        className={
                          isLaunching
                            ? undefined
                            : isLaunchDisabled
                            ? "[&_svg>path]:fill-innoveraZinc-400"
                            : briefStatus === "complete"
                            ? "[&_svg>path]:fill-white"
                            : "[&_svg>path]:fill-innoveraZinc-900"
                        }
                        startAdornment={<InnoveraIcon className="size-4" />}
                        data-testid="project-creation-header-launch-analysis-button"
                      >
                        {t("launchAnalysis")}
                      </AppButton>
                    </span>
                    </PopoverAnchor>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="end" sideOffset={8} className="w-[252px]">
                    <div className="flex flex-col gap-1.5">
                      <FigmaTypography variant="label-2" color="zinc900">{launchTooltip.title}</FigmaTypography>
                      <FigmaTypography variant="body-3" color="zinc600">{launchTooltip.description}</FigmaTypography>
                    </div>
                  </TooltipContent>
                </Tooltip>
                  <PopoverContent
                    side="bottom"
                    align="end"
                    sideOffset={8}
                    className="w-[280px] p-3"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    data-testid="project-creation-header-launch-nudge"
                  >
                    <div className="flex flex-col gap-3">
                      <FigmaTypography variant="body-3" color="zinc900">{t("header.launchNudge")}</FigmaTypography>
                      <div className="flex justify-end">
                        <AppButton size="sm" color="zinc950" onClick={launchNudge?.onDismiss}>
                          {t("header.launchNudgeDismiss")}
                        </AppButton>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleResetConfirm}
        title={t("header.resetTitle")}
        description={t("header.resetDescription")}
        confirmText={t("header.resetConfirm")}
        cancelText={t("common:cancel")}
      />

      <ConfirmationModal
        isOpen={showLaunchConfirm}
        onClose={() => setShowLaunchConfirm(false)}
        onConfirm={handleLaunchConfirm}
        title={launchConfirm.title}
        description={launchConfirm.description}
        confirmText={t("launchAnalysis")}
        cancelText={t("common:cancel")}
        confirmButtonColor="zinc950"
        confirmStartAdornment={<InnoveraIcon className="size-4 [&>path]:fill-white" />}
      />
    </>
  );
}
